import * as React from "react"
import { cn } from "@/utils"
import { Menu, X } from "lucide-react"

// Context para gerenciar estado do sidebar
const SidebarContext = React.createContext({
  isOpen: true,
  isMobile: false,
  toggle: () => {},
  open: () => {},
  close: () => {}
})

export const useSidebar = () => {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider')
  }
  return context
}

const SidebarProvider = ({ children, defaultOpen = true, ...props }) => {
  // ✅ PERSISTÊNCIA: Carregar estado do localStorage
  const [isOpen, setIsOpen] = React.useState(() => {
    const saved = localStorage.getItem('sidebar-state')
    if (saved !== null) {
      return saved === 'true'
    }
    return defaultOpen
  })
  const [isMobile, setIsMobile] = React.useState(false)

  // Detectar se é mobile
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      // Em mobile, começar fechado
      if (mobile) {
        setIsOpen(false)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // ✅ PERSISTÊNCIA: Salvar estado no localStorage quando mudar
  React.useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('sidebar-state', isOpen.toString())
    }
  }, [isOpen, isMobile])

  const toggle = React.useCallback(() => setIsOpen(prev => !prev), [])
  const open = React.useCallback(() => setIsOpen(true), [])
  const close = React.useCallback(() => setIsOpen(false), [])

  // ✅ ATALHO DE TECLADO: Ctrl+B para toggle
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+B ou Cmd+B (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        toggle()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggle])

  const value = React.useMemo(
    () => ({ isOpen, isMobile, toggle, open, close }),
    [isOpen, isMobile, toggle, open, close]
  )

  return (
    <SidebarContext.Provider value={value}>
      <div className="flex min-h-screen relative" {...props}>
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

const Sidebar = React.forwardRef(({ className, ...props }, ref) => {
  const { isOpen, isMobile, close } = useSidebar()

  return (
    <>
      {/* Overlay para mobile */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={close}
        />
      )}

      {/* Sidebar */}
      <div
        ref={ref}
        className={cn(
          "flex h-screen flex-col bg-white border-r border-slate-200 transition-all duration-300 ease-in-out",
          // Mobile: sidebar fixo com overlay
          isMobile ? (
            isOpen
              ? "fixed left-0 top-0 z-50 w-64 shadow-2xl"
              : "fixed -left-64 top-0 z-50 w-64"
          ) : (
            // Desktop: sidebar colapsável, fixa no topo durante a rolagem da página
            // (mesmo motivo do `sticky top-0` já usado no header em Layout.jsx: o
            // conteúdo principal pode ultrapassar 100vh e rolar o documento inteiro)
            isOpen
              ? "sticky top-0 w-64"
              : "sticky top-0 w-20"
          ),
          className
        )}
        {...props}
      />
    </>
  )
})
Sidebar.displayName = "Sidebar"

const SidebarHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col", className)}
    {...props}
  />
))
SidebarHeader.displayName = "SidebarHeader"

const SidebarContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-auto", className)}
    {...props}
  />
))
SidebarContent.displayName = "SidebarContent"

const SidebarGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mb-4", className)}
    {...props}
  />
))
SidebarGroup.displayName = "SidebarGroup"

const SidebarGroupLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mb-2", className)}
    {...props}
  />
))
SidebarGroupLabel.displayName = "SidebarGroupLabel"

const SidebarGroupContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("", className)}
    {...props}
  />
))
SidebarGroupContent.displayName = "SidebarGroupContent"

const SidebarMenu = React.forwardRef(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    className={cn("space-y-1", className)}
    {...props}
  />
))
SidebarMenu.displayName = "SidebarMenu"

const SidebarMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("", className)}
    {...props}
  />
))
SidebarMenuItem.displayName = "SidebarMenuItem"

const SidebarMenuButton = React.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? "span" : "button"
  return (
    <Comp
      ref={ref}
      className={cn(
        "flex w-full items-center rounded-md px-2 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    />
  )
})
SidebarMenuButton.displayName = "SidebarMenuButton"

const SidebarTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const { isOpen, toggle } = useSidebar()

  return (
    <button
      ref={ref}
      onClick={toggle}
      className={cn(
        "inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors",
        "hover:bg-slate-100 active:bg-slate-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        "disabled:pointer-events-none disabled:opacity-50",
        "w-10 h-10 group",
        className
      )}
      aria-label={isOpen ? "Fechar menu (Ctrl+B)" : "Abrir menu (Ctrl+B)"}
      title={isOpen ? "Fechar menu (Ctrl+B)" : "Abrir menu (Ctrl+B)"}
      {...props}
    >
      {/* ✅ ANIMAÇÃO: Rotação suave ao expandir/recolher */}
      <div className={cn(
        "transition-transform duration-300 ease-in-out",
        isOpen ? "rotate-180" : "rotate-0"
      )}>
        {isOpen ? (
          <X className="w-5 h-5 transition-all duration-200 group-hover:scale-110" />
        ) : (
          <Menu className="w-5 h-5 transition-all duration-200 group-hover:scale-110" />
        )}
      </div>
    </button>
  )
})
SidebarTrigger.displayName = "SidebarTrigger"

export {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
}
